import { config } from '@/config';
import axios, { AxiosError, type AxiosInstance } from 'axios';
import { UPDATE_DATA_FLOW_BODY_KEYS } from './api-service.types';
import type {
  CreateDataFlowScheduleRequest,
  CreateDataFlowScheduleResponse,
  GetInstanceSchemaResponse,
  GetNotRegisteredTablesResponse,
  GetRegisteredTablesResponse,
  RunImmediateJobResponse,
  RunJobResponse,
  SearchResponse,
  SearchType,
  UpdateDataFlowRequest,
  UpdateDataFlowResponse,
} from './api-service.types';

function getNormalizedBaseUrl(baseUrl: string): string {
  return baseUrl.trim().replace(/\/+$/, '');
}

function toApiErrorMessage(error: unknown, fallbackMessage: string): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const responseMessage = axiosError.response?.data?.message;
    return responseMessage || axiosError.message || fallbackMessage;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallbackMessage;
}

let adpApiClient: AxiosInstance | null = null;

function getAdpApiClient(): AxiosInstance {
  if (adpApiClient) return adpApiClient;

  const normalizedBaseUrl = getNormalizedBaseUrl(config.adpApiUrl);
  if (!normalizedBaseUrl) {
    throw new Error('API URL is not configured');
  }

  adpApiClient = axios.create({
    baseURL: normalizedBaseUrl,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-tenant-name': config.adpTenantName,
    },
  });

  return adpApiClient;
}

export async function runImmediateJob(sessionId: string): Promise<RunImmediateJobResponse> {
  const trimmedSessionId = sessionId.trim();
  if (!trimmedSessionId) {
    throw new Error('Session ID is required to run the immediate ETL job');
  }

  try {
    const response = await getAdpApiClient().post<RunImmediateJobResponse>(
      `/etl-jobs/${encodeURIComponent(trimmedSessionId)}/run-immediate`
    );

    return response.data;
  } catch (error) {
    throw new Error(toApiErrorMessage(error, 'Failed to run immediate ETL job'));
  }
}

export async function runJob(sessionId: string): Promise<RunJobResponse> {
  const trimmedSessionId = sessionId.trim();
  if (!trimmedSessionId) {
    throw new Error('Session ID is required to run the ETL job');
  }

  try {
    const response = await getAdpApiClient().post<RunJobResponse>(
      `/etl-jobs/${encodeURIComponent(trimmedSessionId)}/run`
    );

    return response.data;
  } catch (error) {
    throw new Error(toApiErrorMessage(error, 'Failed to run ETL job'));
  }
}

export async function createDataFlowSchedule(
  payload: CreateDataFlowScheduleRequest
): Promise<CreateDataFlowScheduleResponse> {
  if (!Array.isArray(payload.job_schedular) || payload.job_schedular.length === 0) {
    throw new Error('job_schedular is required to create data flow schedule');
  }

  try {
    const response = await getAdpApiClient().post<CreateDataFlowScheduleResponse>(
      '/data-flow-schedulers',
      payload
    );

    return response.data;
  } catch (error) {
    throw new Error(toApiErrorMessage(error, 'Failed to create data flow schedule'));
  }
}

function hasAtLeastOneUpdateDataFlowField(payload: UpdateDataFlowRequest): boolean {
  return UPDATE_DATA_FLOW_BODY_KEYS.some((key) => key in payload);
}

export async function updateDataFlow(
  dataFlowId: string,
  payload: UpdateDataFlowRequest
): Promise<UpdateDataFlowResponse> {
  const trimmedDataFlowId = dataFlowId.trim();
  if (!trimmedDataFlowId) {
    throw new Error('Data flow ID is required to update data flow');
  }

  if (!payload || typeof payload !== 'object' || !hasAtLeastOneUpdateDataFlowField(payload)) {
    throw new Error('At least one valid data flow field is required to update data flow');
  }

  try {
    const response = await getAdpApiClient().put<UpdateDataFlowResponse>(
      `/data-flows/${encodeURIComponent(trimmedDataFlowId)}`,
      payload
    );

    return response.data;
  } catch (error) {
    throw new Error(toApiErrorMessage(error, 'Failed to update data flow'));
  }
}

export async function getRegisteredTables(
  targetDataSourceId: string | number
): Promise<GetRegisteredTablesResponse> {
  const id = String(targetDataSourceId).trim();

  if (!id) {
    throw new Error('Target data source ID is required to fetch registered tables');
  }

  try {
    const response = await getAdpApiClient().get<GetRegisteredTablesResponse>(
      `/target-data-sources/registered-tables`,
      {
        params: {
          target_data_source_id: id,
        },
      }
    );

    return response.data;
  } catch (error) {
    throw new Error(
      toApiErrorMessage(error, 'Failed to fetch registered tables')
    );
  }
}

export async function searchEntity(
  type: SearchType,
  query: string
): Promise<SearchResponse> {
  const trimmedQuery = query.trim();

  if (!type) {
    throw new Error('Search type is required');
  }

  if (!trimmedQuery) {
    throw new Error('Search query is required');
  }

  try {
    const response = await getAdpApiClient().get<SearchResponse>('/search', {
      params: {
        type,
        q: trimmedQuery,
      },
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 409) {
        return {
          success: false,
          message:
            error.response?.data?.message || 'Entity already exists',
        };
      }
    }

    throw new Error(toApiErrorMessage(error, 'Search request failed'));
  }
}


export async function getInstanceSchema(
  sourceDataSourceId: string | number,
  type: string
): Promise<GetInstanceSchemaResponse> {
  const id = String(sourceDataSourceId).trim();
  const trimmedType = type.trim();

  if (!id) {
    throw new Error('Source data source ID is required');
  }

  if (!trimmedType) {
    throw new Error('Type is required');
  }

  try {
    const response = await getAdpApiClient().get<GetInstanceSchemaResponse>(
      `/source-data-sources/instance-schema`,
      {
        params: {
          source_data_source_id: id,
          type: trimmedType,
        },
      }
    );

    return response.data;
  } catch (error) {
    throw new Error(
      toApiErrorMessage(error, 'Failed to fetch instance schema')
    );
  }
}

export async function getNotRegisteredTables(
  sourceDataSourceId: string | number,
  type: string
): Promise<GetNotRegisteredTablesResponse> {
  const id = String(sourceDataSourceId).trim();
  const trimmedType = type.trim();

  if (!id) {
    throw new Error('Source data source ID is required');
  }

  if (!trimmedType) {
    throw new Error('Type is required');
  }

  try {
    const response = await getAdpApiClient().get<GetNotRegisteredTablesResponse>(
      `/source-data-sources/not-registered-tables`,
      {
        params: {
          source_data_source_id: id,
          type: trimmedType,
        },
      }
    );

    return response.data;
  } catch (error) {
    throw new Error(
      toApiErrorMessage(error, 'Failed to fetch not registered tables')
    );
  }
}